import { Globe } from "lucide-react";

interface AnalysisMetadata {
  confidence?: number;
  // The Watchlist pipeline's remote_worldwide_analysis payload reports this
  // under remote_confidence instead of confidence — same value, different key.
  remote_confidence?: number;
  reason?: string;
  evidence?: string[];
  restriction_type?: string;
  parse_error?: boolean;
  content_missing?: boolean;
}

function parseMetadata(raw?: string | null): AnalysisMetadata | null {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    return typeof parsed === "object" && parsed !== null ? parsed : null;
  } catch {
    return null;
  }
}

// Different job pipelines report confidence on different scales — LinkedIn's is
// mostly 0-1, TheirStack's is 0-100 — so normalize anything above 1 as already
// being a percentage rather than a fraction to multiply.
function formatConfidence(confidence: number): string {
  const pct = confidence <= 1 ? confidence * 100 : confidence;
  return `${Math.round(pct)}%`;
}

export const REMOTE_SCOPE_CHIP: Record<string, { label: string; className: string }> = {
  worldwide:           { label: "Worldwide Remote",  className: "bg-emerald-500 text-emerald-950 border-emerald-400" },
  pakistan_allowed:    { label: "PK Allowed",         className: "bg-sky-500 text-sky-950 border-sky-400" },
  country_restricted:  { label: "Country Restricted", className: "bg-rose-500 text-rose-950 border-rose-400" },
  region_restricted:   { label: "Region Restricted",  className: "bg-orange-500 text-orange-950 border-orange-400" },
  onsite_hybrid:       { label: "Onsite/Hybrid",     className: "bg-slate-500 text-slate-950 border-slate-400" },
  unclear:             { label: "Unclear",            className: "bg-amber-500 text-amber-950 border-amber-400" },
};

interface Props {
  remoteScope?: string | null;
  analysisJson?: string | null;
  className?: string;
}

/** Remote-scope chip (Worldwide/PK Allowed/Unclear) with a hover popover revealing the
 * underlying LLM analysis — reason, restriction type, and evidence — so a mismatch between
 * the chip and the actual job (e.g. "PK Allowed" on a clearly onsite job) is visible without
 * a database lookup. */
export default function RemoteScopeChip({ remoteScope, analysisJson, className }: Props) {
  if (!remoteScope) return null;
  const entry = REMOTE_SCOPE_CHIP[remoteScope];
  if (!entry) return null;

  const metadata = parseMetadata(analysisJson);
  const confidenceValue = metadata?.confidence ?? metadata?.remote_confidence;

  return (
    <span className={`group relative inline-flex shrink-0 ${className ?? ""}`}>
      <span className={`flex items-center gap-0.5 text-[10px] px-1.5 py-0.5 rounded border font-inter font-medium cursor-default ${entry.className}`}>
        <Globe className="w-2.5 h-2.5" />
        {entry.label}
      </span>

      {metadata && (
        // pt-2 (not mt-2) keeps the gap inside this element's own hoverable box, so moving
        // the mouse from the chip down into the popover never crosses a dead zone that would
        // drop group-hover and hide the popover before the cursor arrives.
        <div className="hidden group-hover:block absolute z-30 top-full right-0 w-64 pt-2">
          <div className="bg-surface-muted border border-border rounded-xl shadow-2xl p-3 text-left">
            <p className="text-[11px] font-semibold text-primary font-inter mb-1.5">Remote-Scope Analysis</p>
            {(metadata.parse_error || metadata.content_missing) && (
              <p className="text-[11px] text-destructive font-inter mb-1.5">
                {metadata.content_missing
                  ? "AI returned no content — unverified."
                  : "AI response could not be parsed — unverified."}
              </p>
            )}
            {confidenceValue !== undefined && (
              <p className="text-[11px] text-muted-foreground font-inter mb-1">
                Confidence: <span className="text-foreground font-medium">{formatConfidence(confidenceValue)}</span>
              </p>
            )}
            {metadata.restriction_type && (
              <p className="text-[11px] text-muted-foreground font-inter mb-1">
                Restriction: <span className="text-foreground font-medium">{metadata.restriction_type}</span>
              </p>
            )}
            {metadata.reason && (
              <p className="text-[11px] text-muted-foreground font-inter leading-snug mb-1.5">{metadata.reason}</p>
            )}
            {metadata.evidence && metadata.evidence.length > 0 && (
              <ul className="list-disc list-inside space-y-0.5">
                {metadata.evidence.map((e, i) => (
                  <li key={i} className="text-[10px] text-muted-foreground font-inter">{e}</li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}
    </span>
  );
}
